import React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  FinanceDeleteDimensionDocument,
  FinanceGetDimensionsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { Dimension, DimensionValue } from '../useDimensionDetail'

type ModalProps = React.ComponentProps<typeof Modal>

const DeleteDimensionModal = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [deleting, setDeleting] = React.useState(false)
  const { company } = useCurrentSession()
  const [deleteDimension] = useMutation(FinanceDeleteDimensionDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceCustomDimension,
  })
  const { dimension, dimensionValues, onClose, onOpenDeleteModal } = useOutletContext<{
    dimension: Dimension
    dimensionValues: DimensionValue[]
    onClose: (success?: boolean) => void
    onOpenDeleteModal: () => void
  }>()

  const handleSubmit = async () => {
    setDeleting(true)
    const result = await deleteDimension({
      variables: {
        id: dimension.id,
        companyId: company.id,
      },
      refetchQueries: [
        {
          query: FinanceGetDimensionsDocument,
          variables: {
            tenant: {
              companyId: company.id,
            },
          },
        },
      ],
    })

    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('finance.modals.delete_dimension.error') })
    })

    if (handled.success) {
      onClose(true)
      navigate(navigation.settings.finance.fullPath())
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.modals.delete_dimension.success'),
      })
    }
    setDeleting(false)
  }

  const goBack = () => {
    navigate(
      navigation.settings.finance.dimensions.dimension.fullPath({
        id: dimension.id,
      })
    )
  }

  useEffect(() => {
    if (dimension) {
      onOpenDeleteModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!dimension) return null

  const canDeleteDimension = dimensionValues?.every(
    (value) => value.customdimensionmembershipsConnection.totalCount === 0
  )

  const removingProps: ModalProps = {
    illustration: 'delete',
    title: i18n.t('finance.modals.delete_dimension.title'),
    description: i18n.t('finance.modals.delete_dimension.description'),
    onClose: () => {
      onClose()
      goBack()
    },
    onSubmit: handleSubmit,
    footer: (
      <Button disabled={deleting} submit>
        {i18n.t('finance.buttons.delete')}
      </Button>
    ),
  }

  const removingNotAllowedProps: ModalProps = {
    illustration: 'alert',
    title: i18n.t('finance.modals.delete_dimension.title_not_allowed'),
    description: i18n.t('finance.modals.delete_dimension.description_not_allowed'),
    onClose: () => {
      onClose()
      goBack()
    },
  }

  const modalProps = canDeleteDimension ? removingProps : removingNotAllowedProps

  return <Modal {...modalProps} />
}

export default DeleteDimensionModal
